/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OutOfServiceReasonProps {
  id: string;
  name: string;
  controllable: boolean;
  description: string | null;
  isVehicleReason: boolean;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class OutOfServiceReasonRecord extends Record<OutOfServiceReasonProps>({
  id: null as any,
  name: null as any,
  controllable: null as any,
  description: null as any,
  isVehicleReason: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    controllable: boolean;
    description: string | null;
    isVehicleReason: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): OutOfServiceReasonRecord {
    if (json == null) {
      throw new Error('OutOfServiceReasonRecord.fromJS: parameter cannot be null');
    }
    return new OutOfServiceReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'OutOfServiceReasonJson.name'),
      controllable: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'controllable', false),
        'OutOfServiceReasonJson.controllable',
      ),
      description: json.description,
      isVehicleReason: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isVehicleReason', false),
        'OutOfServiceReasonJson.isVehicleReason',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    controllable: boolean;
    description: string | null;
    isVehicleReason: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [OutOfServiceReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('OutOfServiceReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OutOfServiceReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'OutOfServiceReasonJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      controllable: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'controllable', false),
            'OutOfServiceReasonJson.controllable',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      description: tryOrDefault(
        () => json.description,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isVehicleReason: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isVehicleReason', false),
            'OutOfServiceReasonJson.isVehicleReason',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: OutOfServiceReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('OutOfServiceReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OutOfServiceReasonViewModelBase implements OutOfServiceReasonProps {
  protected readonly _props: OutOfServiceReasonProps;
  private _json?: OutOfServiceReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get controllable(): boolean {
    return this._props.controllable;
  }
  get description(): string | null {
    return this._props.description;
  }
  get isVehicleReason(): boolean {
    return this._props.isVehicleReason;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: OutOfServiceReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        controllable: this._props.controllable,
        description: this._props.description,
        isVehicleReason: this._props.isVehicleReason,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllOutOfServiceReasonEntities = (state: AppState) =>
  state.entityStores.outOfServiceReason.entities;

export const selectOutOfServiceReasonById = (id: string) => (state: AppState) =>
  state.entityStores.outOfServiceReason.entities.get(id);

export const selectOutOfServiceReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.outOfServiceReason.meta.get(id);

export const selectOutOfServiceReasonAndMetadataById = (id: string) =>
  createSelector(
    selectOutOfServiceReasonById(id),
    selectOutOfServiceReasonMetadataById(id),
    (outOfServiceReason, meta) => ({ outOfServiceReason, meta }),
  );

export function selectOutOfServiceReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllOutOfServiceReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, OutOfServiceReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectOutOfServiceReasonEntitiesByName(name: string) {
  return createSelector(selectAllOutOfServiceReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectOutOfServiceReasonEntitiesByControllable(controllable: boolean) {
  return createSelector(selectAllOutOfServiceReasonEntities, (entities) =>
    entities.filter((e) => e.controllable === controllable).valueSeq(),
  );
}

export function selectOutOfServiceReasonEntitiesByDescription(description: string) {
  return createSelector(selectAllOutOfServiceReasonEntities, (entities) =>
    entities.filter((e) => e.description === description).valueSeq(),
  );
}

export function selectOutOfServiceReasonEntitiesByIsVehicleReason(isVehicleReason: boolean) {
  return createSelector(selectAllOutOfServiceReasonEntities, (entities) =>
    entities.filter((e) => e.isVehicleReason === isVehicleReason).valueSeq(),
  );
}
//#endregion Selectors
